import { Dialog } from '@components/dialog';
import { TQuoteApiResponse } from '@models/quotes';
import { Box, List, ListItem, Typography } from '@mui/material';
import { useSearchOfferingIneligibility } from '@services/ineligibility/useSearchOfferingIneligibility';
import { convertToISOStringIfDate } from '@utils/date';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { groupBy } from 'lodash';
import { useMemo } from 'react';

type IneligibleExposureItemListDialogProps = {
  quote: TQuoteApiResponse;
  isOpen: boolean;
  onCloseModal: () => void;
};

export const IneligibleExposureItemListDialog = ({
  quote,
  isOpen,
  onCloseModal,
}: IneligibleExposureItemListDialogProps) => {
  const { isMobileScreen, isSmallScreen } = useGetScreenSize();

  const offeringId = quote?.offering?.id;
  const productOptionId = quote?.productOption?.id;
  const quoteCreatedDate = quote?.createdAt;

  const { offeringIneligibility } = useSearchOfferingIneligibility(
    { offeringId },
    { productOptionId, effectiveDate: convertToISOStringIfDate(quoteCreatedDate) },
  );

  const groupedIneligibilities = useMemo(() => {
    const offeringIneligibilityItems = offeringIneligibility?.items;

    const result = groupBy(offeringIneligibilityItems, (el) => el.ineligibilityGroup.label);
    Object.entries(result).forEach((item) => {
      const k = item[0];
      const v = item[1];
      result[k] = v.sort((a, b) => a.orderBy - b.orderBy);
    });
    return result;
  }, [offeringIneligibility]);

  let columnCount = 3;
  if (isMobileScreen) {
    columnCount = 1;
  }
  if (isSmallScreen) {
    columnCount = 2;
  }

  return (
    <Dialog
      open={isOpen}
      handleCloseDialog={onCloseModal}
      secondaryBtnAction={onCloseModal}
      secondaryBtnText='Exit'
      title={`Ineligible Exposures for ${quote?.offering?.label ?? quote?.offering?.insuranceProduct?.name}`}
      size='extraLarge'
    >
      <Box sx={{ margin: '10px 28px 40px' }}>
        <Typography variant='body' display='block' sx={{ mb: 3 }}>
          The following operations and exposures are ineligible:
        </Typography>
        {/* // for each secton // render a label // render a mui 3 column grid of items with list item bullets */}
        {Object.entries(groupedIneligibilities).map(([label, items]) => (
          <Box key={label} sx={{ marginBottom: 5 }}>
            {label && (
              <Typography variant='body' fontWeight='bold'>
                {label}
              </Typography>
            )}
            <Box
              sx={{
                columnCount,

                // columnCount while in theory supported in safari si not working.
                // this fixes it, but should be used only as a last-resort
                willChange: 'transform',
              }}
            >
              <List dense sx={{ listStyleType: 'disc', padding: '5px 0 10px 20px' }}>
                {items.map((item) => (
                  <ListItem key={item.id} sx={{ display: 'list-item', padding: '0' }}>
                    <Typography variant='bodySmall' color='text.main'>
                      {item.description}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        ))}
      </Box>
    </Dialog>
  );
};
